"use client";

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Plane, Users, Briefcase, Baby, Car, Building, User, Mail, Phone, MessageSquare, CircleCheckBig as CheckCircle2, CircleAlert as AlertCircle, Loader as Loader2, Send, ChevronRight, ChevronLeft, Pencil, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Link } from "@/components/common/Link";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
interface FormFieldDefinition {
  id: string;
  key: string;
  type: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  default?: any;
  options?: Array<{
    value: string;
    label: string;
    price?: number;
  }>;
  config?: Record<string, any>;
  validation?: {
    messages?: {
      required?: string;
      invalid?: string;
    };
  };
}
interface FormDefinition {
  id: number;
  key: string;
  name: string;
  description?: string;
  fields: FormFieldDefinition[];
  submit?: {
    label: string;
    processing_label?: string;
  };
  spam?: {
    token: string;
    honeypot?: {
      field: string;
    } | null;
    min_seconds?: number;
    challenge?: any;
  };
}
const STEP_TITLES = ["Fahrtdetails", "Buchungsdetails", "Kontaktdaten", "Prüfen & Absenden"];
export default function BookingForm() {
  const [formDef, setFormDef] = useState<FormDefinition | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Field values keyed by field id
  const [values, setValues] = useState<Record<string, string>>({});
  const [hpValue, setHpValue] = useState("");
  const [fetchTimestamp, setFetchTimestamp] = useState<number>(0);

  // Step state (0=Fahrtdetails, 1=Buchungsdetails, 2=Kontaktdaten, 3=Review)
  const [currentStep, setCurrentStep] = useState<number>(0);

  // Submit states
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, {
    message: string;
  }>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submittedData, setSubmittedData] = useState<Record<string, string> | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [serverMessageHtml, setServerMessageHtml] = useState<string>("");
  const FORM_KEY = "private_transfer_booking_de";

  // Fetch form definition
  useEffect(() => {
    let isMounted = true;
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      setLoadError("Integrations-Client ist nicht verfügbar.");
      setLoading(false);
      return;
    }
    client.pluginRequest(`wvc-forms/v1/forms/${FORM_KEY}`).then((data: FormDefinition) => {
      if (!isMounted) return;
      setFormDef(data);
      setFetchTimestamp(Date.now());
      // Initialize default values
      const initialVals: Record<string, string> = {};
      if (data.fields) {
        data.fields.forEach(f => {
          initialVals[f.id] = f.default as string || "";
        });
      }
      setValues(initialVals);
      setLoading(false);
    }).catch((err: any) => {
      if (!isMounted) return;
      if (err?.status === 404) {
        // Recovery attempt
        client.pluginRequest("wvc-forms/v1/forms").then((listData: {
          items: Array<{
            key: string;
            name: string;
          }>;
        }) => {
          if (!isMounted) return;
          const match = listData?.items?.find(i => i.key === FORM_KEY) || listData?.items?.find(i => i.key.includes("de") && i.name.toLowerCase().includes("transfer")) || listData?.items?.find(i => i.name.toLowerCase().includes("transfer")) || (listData?.items?.length === 1 ? listData.items[0] : null);
          if (match) {
            return client.pluginRequest(`wvc-forms/v1/forms/${match.key}`).then((recovered: FormDefinition) => {
              if (!isMounted) return;
              setFormDef(recovered);
              setFetchTimestamp(Date.now());
              const initRecoveryVals: Record<string, string> = {};
              recovered.fields?.forEach(f => {
                initRecoveryVals[f.id] = f.default as string || "";
              });
              setValues(initRecoveryVals);
              setLoading(false);
            });
          } else {
            setLoadError("Das Buchungsformular ist derzeit nicht verfügbar.");
            setLoading(false);
          }
        }).catch(() => {
          if (isMounted) {
            setLoadError("Buchungsformular konnte nicht geladen werden.");
            setLoading(false);
          }
        });
      } else {
        setLoadError("Verbindung zum Buchungsservice fehlgeschlagen.");
        setLoading(false);
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);
  const getFieldByMatchedKey = (candidates: string[]): FormFieldDefinition | undefined => {
    if (!formDef?.fields) return undefined;
    return formDef.fields.find(f => candidates.includes(f.key));
  };
  const handleInputChange = (fieldId: string, value: string) => {
    setValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
    if (fieldErrors[fieldId]) {
      setFieldErrors(prev => {
        const next = {
          ...prev
        };
        delete next[fieldId];
        return next;
      });
    }
  };

  // Pre-matched field definitions from schema
  const fieldPickup = getFieldByMatchedKey(["pickup_location", "pickup", "pickup_loc"]);
  const fieldDest = getFieldByMatchedKey(["destination", "dropoff_location", "dest"]);
  const fieldType = getFieldByMatchedKey(["transfer_type", "type"]);
  const fieldFlight = getFieldByMatchedKey(["flight_number", "flight"]);
  const fieldDate = getFieldByMatchedKey(["transfer_date", "date"]);
  const fieldTime = getFieldByMatchedKey(["pickup_landing_time", "time", "pickup_time"]);
  const fieldPax = getFieldByMatchedKey(["number_of_passengers", "passengers", "pax"]);
  const fieldBags = getFieldByMatchedKey(["number_of_suitcases", "suitcases", "bags", "luggage"]);
  const fieldChildseat = getFieldByMatchedKey(["child_seat_request", "child_seat", "childseat"]);
  const fieldChildAge = getFieldByMatchedKey(["child_age", "childs_age"]);
  const fieldVehicle = getFieldByMatchedKey(["vehicle_preference", "vehicle"]);
  const fieldHotel = getFieldByMatchedKey(["hotel_or_accommodation_name", "hotel", "hotel_name"]);
  const fieldName = getFieldByMatchedKey(["full_name", "name", "customer_name"]);
  const fieldEmail = getFieldByMatchedKey(["email_address", "email"]);
  const fieldWhatsapp = getFieldByMatchedKey(["whatsapp_number", "whatsapp", "phone"]);
  const fieldNotes = getFieldByMatchedKey(["additional_message_or_flight_notes", "notes", "message", "additional_notes"]);

  // Return Journey fields
  const fieldReturnDate = getFieldByMatchedKey(["return_pickup_date", "return_date"]);
  const fieldReturnTime = getFieldByMatchedKey(["return_pickup_time", "return_time"]);
  const fieldReturnLocation = getFieldByMatchedKey(["return_pickup_location", "return_location", "return_pickup"]);
  const fieldReturnFlight = getFieldByMatchedKey(["return_flight_number", "return_flight"]);
  const fieldReturnAirport = getFieldByMatchedKey(["return_destination_airport", "return_airport", "return_destination"]);

  // Determine if round trip is currently selected
  const currentTransferType = fieldType ? values[fieldType.id] || "" : "";
  const isRoundTrip = currentTransferType.toLowerCase() === "round_trip" || currentTransferType.toLowerCase() === "roundtrip" || currentTransferType.toLowerCase().includes("hin_und_rueck") || currentTransferType.toLowerCase().includes("round");

  // Determine if child seat was requested
  const childSeatValue = fieldChildseat ? (values[fieldChildseat.id] || "").trim().toLowerCase() : "";
  const isChildSeatRequested = Boolean(childSeatValue && childSeatValue !== "no" && childSeatValue !== "nein" && childSeatValue !== "none" && childSeatValue !== "0" && childSeatValue !== "keine" && childSeatValue !== "kein" && childSeatValue !== "kein_kindersitz" && childSeatValue !== "false");

  // Per-step client-side validation
  const validateStepFields = (step: number): boolean => {
    if (!formDef) return true;
    const errors: Record<string, {
      message: string;
    }> = {};
    let stepFields: (FormFieldDefinition | undefined)[] = [];
    if (step === 0) {
      stepFields = [fieldPickup, fieldDest, fieldType, fieldFlight, fieldDate, fieldTime];
      if (isRoundTrip) {
        stepFields.push(fieldReturnDate, fieldReturnTime, fieldReturnLocation, fieldReturnFlight, fieldReturnAirport);
      }
    } else if (step === 1) {
      stepFields = [fieldPax, fieldBags, fieldChildseat, ...(isChildSeatRequested && fieldChildAge ? [fieldChildAge] : []), fieldVehicle, fieldHotel];
    } else if (step === 2) {
      stepFields = [fieldName, fieldEmail, fieldWhatsapp, fieldNotes, ...unmappedFields];
    }
    stepFields.forEach(field => {
      if (!field || field.type === "hidden" || field.type === "heading" || field.type === "paragraph") {
        return;
      }
      const isRequired = field.required || fieldChildAge && field.id === fieldChildAge.id && isChildSeatRequested;
      if (isRequired) {
        const val = values[field.id];
        const isEmpty = val === undefined || val === null || typeof val === "string" && val.trim() === "";
        if (isEmpty) {
          errors[field.id] = {
            message: field.validation?.messages?.required || "Dieses Feld ist erforderlich."
          };
        }
      }
    });
    if (Object.keys(errors).length > 0) {
      setFieldErrors(prev => ({
        ...prev,
        ...errors
      }));
      setFormError("Bitte füllen Sie alle erforderlichen Felder in diesem Schritt aus.");
      return false;
    }
    setFormError(null);
    return true;
  };
  const handleNext = () => {
    if (validateStepFields(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };
  const handleBack = () => {
    setFormError(null);
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formDef || submitting) return;
    setFormError(null);
    setFieldErrors({});

    // Client-side required-field validation
    const returnFieldIds = new Set([fieldReturnDate?.id, fieldReturnTime?.id, fieldReturnLocation?.id, fieldReturnFlight?.id, fieldReturnAirport?.id].filter(Boolean) as string[]);
    const clientErrors: Record<string, {
      message: string;
    }> = {};
    formDef.fields.forEach(field => {
      if (field.type === "hidden" || field.type === "heading" || field.type === "paragraph") {
        return;
      }
      const isReturnField = returnFieldIds.has(field.id) || field.key.startsWith("return_");
      const isChildAgeField = fieldChildAge && field.id === fieldChildAge.id;

      // Skip validating child age if not requested
      if (isChildAgeField && !isChildSeatRequested) {
        return;
      }
      const isRequired = (field.required || isChildAgeField && isChildSeatRequested) && (!isReturnField || isRoundTrip);
      if (isRequired) {
        const val = values[field.id];
        const isEmpty = val === undefined || val === null || typeof val === "string" && val.trim() === "";
        if (isEmpty) {
          clientErrors[field.id] = {
            message: field.validation?.messages?.required || "Dieses Feld ist erforderlich."
          };
        }
      }
    });
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      setFormError("Bitte füllen Sie alle erforderlichen Felder aus.");
      return;
    }

    // Enforce spam minimum seconds safeguard
    const minSeconds = formDef.spam?.min_seconds || 3;
    const elapsedSeconds = (Date.now() - fetchTimestamp) / 1000;
    if (elapsedSeconds < minSeconds) {
      const waitMs = Math.ceil((minSeconds - elapsedSeconds) * 1000);
      await new Promise(resolve => setTimeout(resolve, waitMs));
    }
    setSubmitting(true);
    const client = getWvcClient();
    try {
      // Filter out return journey fields if not round trip, and child age if not requested
      const submissionValues: Record<string, string> = {
        ...values
      };
      if (!isRoundTrip) {
        returnFieldIds.forEach(id => {
          delete submissionValues[id];
        });
      }
      if (!isChildSeatRequested && fieldChildAge) {
        delete submissionValues[fieldChildAge.id];
      }
      const payload: Record<string, any> = {
        values: submissionValues,
        token: formDef.spam?.token,
        meta: {
          source_url: typeof window !== "undefined" ? window.location.href : "",
          referrer: typeof document !== "undefined" ? document.referrer : ""
        }
      };
      if (formDef.spam?.honeypot?.field) {
        payload.hp = {
          [formDef.spam.honeypot.field]: hpValue
        };
      }
      const res = await client.pluginRequest(`wvc-forms/v1/forms/${formDef.key}/submit`, {
        method: "POST",
        data: payload
      });
      if (res && res.success) {
        setSubmittedData({
          ...submissionValues
        });
        setIsSuccess(true);
        if (res.confirmation?.message) {
          setServerMessageHtml(res.confirmation.message);
        }
        if (res.confirmation?.type === "redirect" && res.confirmation?.url) {
          window.location.assign(res.confirmation.url);
        }
      } else if (res && res.code === "frms_validation_failed") {
        const serverErrors: Record<string, {
          message: string;
        }> = {};
        if (res.errors && typeof res.errors === "object") {
          Object.entries(res.errors).forEach(([errKey, errVal]: [string, any]) => {
            const msg = typeof errVal === "string" ? errVal : errVal?.message || "Bitte überprüfen Sie dieses Feld.";
            const matchedField = formDef.fields.find(f => f.id === errKey || f.key === errKey);
            const targetId = matchedField ? matchedField.id : errKey;
            serverErrors[targetId] = {
              message: msg
            };
          });
        }
        setFieldErrors(serverErrors);
        setFormError(res.message || "Bitte korrigieren Sie die markierten Felder.");
      } else if (res && res.code === "frms_invalid_token") {
        setFormError("Sitzung abgelaufen. Bitte laden Sie die Seite neu, um die Anfrage zu senden.");
      } else {
        setFormError(res?.message || "Buchungsanfrage fehlgeschlagen. Bitte versuchen Sie es erneut.");
      }
    } catch (err: any) {
      setFormError(err?.message || "Ein Netzwerkfehler ist aufgetreten. Bitte überprüfen Sie Ihre Internetverbindung.");
    } finally {
      setSubmitting(false);
    }
  };

  // Helper for human-readable option labels
  const getDisplayValue = (field: FormFieldDefinition, rawVal: string): string => {
    if (!rawVal) return "";
    if (field.options && field.options.length > 0) {
      const matchedOption = field.options.find(opt => opt.value.toLowerCase() === rawVal.toLowerCase() || opt.value === rawVal);
      if (matchedOption) {
        return matchedOption.label;
      }
    }
    return rawVal;
  };

  // WhatsApp message generation in German
  const generateWhatsAppUrl = () => {
    const businessPhone = "201010634627";
    if (!formDef || !submittedData) {
      return `https://wa.me/${businessPhone}?text=${encodeURIComponent("Hallo Hurghada Transfer Hub, ich möchte einen privaten Transfer anfragen.")}`;
    }
    const lines: string[] = ["*Neue Privattransfer-Anfrage — Hurghada Transfer Hub*", "--------------------------------"];
    const addFieldLine = (field?: FormFieldDefinition) => {
      if (!field) return;
      const rawVal = submittedData[field.id];
      if (rawVal !== undefined && rawVal !== null && typeof rawVal === "string" && rawVal.trim() !== "") {
        const displayVal = getDisplayValue(field, rawVal.trim());
        lines.push(`*${field.label}:* ${displayVal}`);
      }
    };

    // 1. Route & Schedule
    const routeFields = [fieldPickup, fieldDest, fieldType, fieldFlight, fieldDate, fieldTime];
    lines.push("*[Route & Zeitplan]*");
    routeFields.forEach(addFieldLine);

    // 2. Return Journey (when applicable)
    const returnFields = [fieldReturnDate, fieldReturnTime, fieldReturnLocation, fieldReturnFlight, fieldReturnAirport];
    const hasReturnData = returnFields.some(f => f && submittedData[f.id] && submittedData[f.id].trim() !== "");
    if (isRoundTrip || hasReturnData) {
      lines.push("");
      lines.push("*[Rückfahrt]*");
      returnFields.forEach(addFieldLine);
    }

    // 3. Passengers & Vehicle
    lines.push("");
    lines.push("*[Passagiere & Fahrzeug]*");
    const paxVehicleFields = [fieldPax, fieldBags, fieldChildseat, ...(isChildSeatRequested && fieldChildAge ? [fieldChildAge] : []), fieldVehicle, fieldHotel];
    paxVehicleFields.forEach(addFieldLine);

    // 4. Contact Details
    lines.push("");
    lines.push("*[Kontaktdaten]*");
    const contactFields = [fieldName, fieldEmail, fieldWhatsapp];
    contactFields.forEach(addFieldLine);

    // 5. Notes
    if (fieldNotes) {
      const rawNotes = submittedData[fieldNotes.id];
      if (rawNotes && rawNotes.trim()) {
        lines.push("");
        lines.push("*[Zusätzliche Anmerkungen]*");
        addFieldLine(fieldNotes);
      }
    }

    // Handled fields set to avoid omitting unmapped fields
    const handledIds = new Set([...routeFields, ...returnFields, ...paxVehicleFields, ...contactFields, fieldNotes].filter(Boolean).map(f => f!.id));
    const extraFilled = (formDef.fields || []).filter(f => !handledIds.has(f.id) && submittedData[f.id]?.trim());
    if (extraFilled.length > 0) {
      lines.push("");
      lines.push("*[Weitere Details]*");
      extraFilled.forEach(addFieldLine);
    }
    lines.push("--------------------------------");
    lines.push("Bitte bestätigen Sie meine Buchungszeiten und Fahrerdetails.");
    const text = lines.join("\n");
    return `https://wa.me/${businessPhone}?text=${encodeURIComponent(text)}`;
  };

  // Set of matched field IDs and keys to avoid duplicate generic rendering
  const matchedFieldObjects = [fieldPickup, fieldDest, fieldType, fieldFlight, fieldDate, fieldTime, fieldPax, fieldBags, fieldChildseat, fieldChildAge, fieldVehicle, fieldHotel, fieldName, fieldEmail, fieldWhatsapp, fieldNotes, fieldReturnDate, fieldReturnTime, fieldReturnLocation, fieldReturnFlight, fieldReturnAirport].filter(Boolean) as FormFieldDefinition[];
  const mappedIds = new Set(matchedFieldObjects.map(f => f.id));
  const mappedKeys = new Set(matchedFieldObjects.map(f => f.key));
  const unmappedFields = (formDef?.fields || []).filter(f => !mappedIds.has(f.id) && !mappedKeys.has(f.key) && f.type !== "hidden");

  // Review helper: render a grouped summary card with an edit jump link
  const renderReviewGroup = (title: string, stepIndex: number, fields: (FormFieldDefinition | undefined)[]) => {
    const filledFields = fields.filter((f): f is FormFieldDefinition => Boolean(f && values[f.id] && values[f.id].trim() !== ""));
    if (filledFields.length === 0) return null;
    return <div className="rounded-md border border-border bg-muted/40 p-4 sm:p-5">
        <div className="flex items-center justify-between pb-3 mb-3 border-b border-border">
          <h4 className="text-sm font-semibold text-foreground">{title}</h4>
          <Button type="button" variant="ghost" size="sm" onClick={() => setCurrentStep(stepIndex)} className="h-7 px-2.5 text-xs text-primary hover:text-primary/90 hover:bg-primary/10 gap-1.5 font-medium cursor-pointer">
            <Pencil className="size-3" />
            Bearbeiten
          </Button>
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3.5 text-sm">
          {filledFields.map(field => <div key={field.id} className="min-w-0">
              <dt className="text-xs text-muted-foreground font-medium">{field.label}</dt>
              <dd className="text-sm font-medium text-foreground mt-0.5 truncate break-words">
                {getDisplayValue(field, values[field.id])}
              </dd>
            </div>)}
        </dl>
      </div>;
  };
  return <section id="request-your-private-transfer" data-nav="light" className="bg-background text-foreground py-16 md:py-20 lg:py-24">
      <div className="mx-auto max-w-[1136px] px-6">
        {/* Header Block */}
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-12">
          <div className="flex items-center justify-center gap-2 mb-3" aria-hidden="true">
            <span className="h-px w-8 bg-border" />
            <span className="size-1.5 rounded-full bg-primary" />
            <span className="h-px w-8 bg-border" />
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-foreground font-default">
            Privattransfer anfragen
          </h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed">
            Senden Sie uns Ihre Reisedaten und wir bestätigen Ihren Transfer per WhatsApp oder E-Mail. Keine Online-Zahlung erforderlich – Bezahlung bei Ankunft.
          </p>
        </div>

        {/* 12-Column Grid Layout framing columns 3 to 10 */}
        <div className="grid grid-cols-12 gap-4 sm:gap-6">
          <div className="col-span-12 lg:col-start-3 lg:col-span-8">
            {loading ? <div className="bg-card border border-border rounded-md p-10 flex flex-col items-center justify-center shadow-xs">
                <Loader2 className="size-8 text-primary animate-spin mb-3" />
                <p className="text-sm text-muted-foreground">
                  Das Anfrageformular wird geladen …
                </p>
              </div> : loadError ? <div className="bg-card border border-border rounded-md p-8 shadow-xs">
                <Alert variant="destructive">
                  <AlertCircle className="size-4" />
                  <AlertTitle>Hinweis</AlertTitle>
                  <AlertDescription>{loadError}</AlertDescription>
                </Alert>
                <div className="mt-6 text-center">
                  <p className="text-sm text-muted-foreground mb-4">
                    Sie können auch direkt über unsere rund um die Uhr besetzte Zentrale in Hurghada buchen:
                  </p>
                  <Button asChild className="bg-[#27BA2A] text-white hover:bg-[#22a025]">
                    <Link to="https://wa.me/201010634627?text=Hallo%2C+ich+m%C3%B6chte+einen+privaten+Transfer+anfragen" newTab>
                      Per WhatsApp buchen (+20 10 10634627)
                    </Link>
                  </Button>
                </div>
              </div> : isSuccess ? <motion.div initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4
          }} className="bg-card border border-border rounded-md p-6 sm:p-10 shadow-xs text-center">
                <div className="size-14 rounded-full bg-accent text-accent-foreground flex items-center justify-center mx-auto mb-4 border border-primary/30">
                  <CheckCircle2 className="size-8 text-primary" />
                </div>
                <h2 className="text-2xl font-semibold text-foreground mb-2">
                  Buchungsanfrage erhalten!
                </h2>
                <div className="text-muted-foreground text-sm sm:text-base leading-relaxed max-w-lg mx-auto mb-8">
                  {serverMessageHtml ? <div dangerouslySetInnerHTML={{
                __html: serverMessageHtml
              }} className="prose prose-sm text-muted-foreground" /> : <p>
                      Vielen Dank. Wir haben Ihre Buchungsanfrage erhalten. Unser Team prüft Ihre Angaben und meldet sich in Kürze per WhatsApp oder E-Mail bei Ihnen mit den Bestätigungs- und Fahrerdetails.
                    </p>}
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Button asChild className="w-full sm:w-auto bg-[#27BA2A] text-white hover:bg-[#22a025]">
                    <Link to={generateWhatsAppUrl()} newTab>
                      <Send className="size-4 mr-2" />
                      Details per WhatsApp senden
                    </Link>
                  </Button>
                  <Button variant="outline" onClick={() => {
                setIsSuccess(false);
                setSubmittedData(null);
                setCurrentStep(0);
              }} className="w-full sm:w-auto">
                    Weitere Fahrt anfragen
                  </Button>
                </div>
              </motion.div> : <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.45
          }} className="bg-card border border-border rounded-md p-6 sm:p-8 md:p-10 shadow-xs">
                {/* 4-Step Progress Indicator */}
                <div className="mb-8 pb-6 border-b border-border">
                  {/* Desktop Step Indicator (sm and above) */}
                  <div className="hidden sm:grid grid-cols-4 gap-2 relative">
                    {STEP_TITLES.map((title, idx) => {
                  const isCompleted = currentStep > idx;
                  const isCurrent = currentStep === idx;
                  return <div key={title} className="flex flex-col items-center text-center relative group">
                          {/* Connector Hairline to next item */}
                          {idx < STEP_TITLES.length - 1 && <div aria-hidden="true" className={`absolute top-4 left-1/2 w-full h-[2px] -z-0 transition-colors ${currentStep > idx ? "bg-chart-1" : "bg-border"}`} />}
                          {/* Step Pill / Badge */}
                          <div className={`size-8 rounded-full flex items-center justify-center text-xs font-semibold z-10 transition-colors ${isCurrent ? "bg-primary text-primary-foreground ring-4 ring-primary/20 shadow-xs" : isCompleted ? "bg-chart-1 text-white border border-chart-1 shadow-xs" : "bg-muted text-muted-foreground border border-border"}`}>
                            {isCompleted ? <Check className="size-4 stroke-[2.5]" /> : idx + 1}
                          </div>
                          <span className={`mt-2 text-xs font-medium transition-colors ${isCurrent ? "text-foreground font-semibold" : isCompleted ? "text-foreground" : "text-muted-foreground"}`}>
                            {title}
                          </span>
                        </div>;
                })}
                  </div>

                  {/* Mobile Condensed Step Indicator */}
                  <div className="sm:hidden">
                    <div className="flex items-center justify-between gap-1 mb-2">
                      {STEP_TITLES.map((_, idx) => {
                    const isCompleted = currentStep > idx;
                    const isCurrent = currentStep === idx;
                    return <div key={idx} className={`flex-1 h-1.5 rounded-full transition-colors ${isCurrent ? "bg-primary" : isCompleted ? "bg-chart-1" : "bg-border"}`} />;
                  })}
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span className="font-semibold text-foreground">
                        Schritt {currentStep + 1} von 4:{" "}
                        <span className="text-primary">{STEP_TITLES[currentStep]}</span>
                      </span>
                      <span>{Math.round((currentStep + 1) / 4 * 100)}%</span>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmit} noValidate className="space-y-6">
                  {/* Honeypot Decoy Input */}
                  {formDef?.spam?.honeypot?.field && <div aria-hidden="true" style={{
                position: "absolute",
                width: "1px",
                height: "1px",
                padding: 0,
                margin: "-1px",
                overflow: "hidden",
                clip: "rect(0,0,0,0)",
                whiteSpace: "nowrap",
                border: 0
              }}>
                      <label htmlFor={formDef.spam.honeypot.field}>Sicherheitsüberprüfung</label>
                      <input type="text" id={formDef.spam.honeypot.field} name={formDef.spam.honeypot.field} tabIndex={-1} autoComplete="off" value={hpValue} onChange={e => setHpValue(e.target.value)} />
                    </div>}

                  {formError && <Alert variant="destructive">
                      <AlertCircle className="size-4" />
                      <AlertTitle>Angaben überprüfen</AlertTitle>
                      <AlertDescription>{formError}</AlertDescription>
                    </Alert>}

                  {/* STEP 0: Trip Details */}
                  {currentStep === 0 && <div className="space-y-6">
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground pb-2 border-b border-border mb-4">
                          Route & Zeitplan
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {/* Pickup Location */}
                          {fieldPickup && <div className="space-y-1.5">
                              <Label htmlFor={fieldPickup.id} className="text-foreground text-sm font-medium leading-normal flex flex-wrap items-center gap-1 break-words">
                                <span>{fieldPickup.label}</span>
                                {fieldPickup.required && <span className="text-destructive"> *</span>}
                              </Label>
                              <div className="relative">
                                <MapPin className="size-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                                <Input id={fieldPickup.id} value={values[fieldPickup.id] || ""} onChange={e => handleInputChange(fieldPickup.id, e.target.value)} placeholder={fieldPickup.placeholder || "z. B. Flughafen Hurghada Terminal 1"} className="pl-11 text-base md:text-sm truncate" required={fieldPickup.required} />
                              </div>
                              {fieldErrors[fieldPickup.id] && <p className="text-xs text-destructive">{fieldErrors[fieldPickup.id].message}</p>}
                            </div>}

                          {/* Destination */}
                          {fieldDest && <div className="space-y-1.5">
                              <Label htmlFor={fieldDest.id} className="text-foreground text-sm font-medium leading-normal flex flex-wrap items-center gap-1 break-words">
                                <span>{fieldDest.label}</span>
                                {fieldDest.required && <span className="text-destructive"> *</span>}
                              </Label>
                              <div className="relative">
                                <MapPin className="size-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                                <Input id={fieldDest.id} value={values[fieldDest.id] || ""} onChange={e => handleInputChange(fieldDest.id, e.target.value)} placeholder={fieldDest.placeholder || "z. B. El Gouna, Makadi Bay, Soma Bay"} className="pl-11 text-base md:text-sm truncate" required={fieldDest.required} />
                              </div>
                              {fieldErrors[fieldDest.id] && <p className="text-xs text-destructive">{fieldErrors[fieldDest.id].message}</p>}
                            </div>}

                          {/* Transfer Type (Arrival / Departure / Round Trip) */}
                          {fieldType && <div className="space-y-1.5">
                              <Label htmlFor={fieldType.id} className="text-foreground text-sm font-medium leading-normal flex flex-wrap items-center gap-1 break-words">
                                <span>{fieldType.label}</span>
                                {fieldType.required && <span className="text-destructive"> *</span>}
                              </Label>
                              {fieldType.options && fieldType.options.length > 0 ? <Select value={values[fieldType.id] || ""} onValueChange={val => handleInputChange(fieldType.id, val)}>
                                  <SelectTrigger id={fieldType.id} className="w-full min-w-0 text-base md:text-sm truncate">
                                    <SelectValue placeholder="Transferrichtung auswählen" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {fieldType.options.map((opt, i) => <SelectItem key={opt.value} value={opt.value} data-index={i} className="text-base md:text-sm truncate">
                                        {opt.label}
                                      </SelectItem>)}
                                  </SelectContent>
                                </Select> : <Input id={fieldType.id} value={values[fieldType.id] || ""} onChange={e => handleInputChange(fieldType.id, e.target.value)} placeholder="Ankunft, Abflug oder Hin- und Rückfahrt" className="text-base md:text-sm truncate" required={fieldType.required} />}
                              {fieldErrors[fieldType.id] && <p className="text-xs text-destructive">{fieldErrors[fieldType.id].message}</p>}
                            </div>}

                          {/* Flight Number */}
                          {fieldFlight && <div className="space-y-1.5">
                              <Label htmlFor={fieldFlight.id} className="text-foreground text-sm font-medium leading-normal flex flex-wrap items-center gap-1 break-words">
                                <span>{fieldFlight.label}</span>
                                {fieldFlight.required && <span className="text-destructive"> *</span>}
                              </Label>
                              <div className="relative">
                                <Plane className="size-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                                <Input id={fieldFlight.id} value={values[fieldFlight.id] || ""} onChange={e => handleInputChange(fieldFlight.id, e.target.value)} placeholder={fieldFlight.placeholder || "z. B. MS703 oder DE1402"} className="pl-11 text-base md:text-sm truncate" required={fieldFlight.required} />
                              </div>
                              {fieldErrors[fieldFlight.id] && <p className="text-xs text-destructive">{fieldErrors[fieldFlight.id].message}</p>}
                            </div>}

                          {/* Transfer Date */}
                          {fieldDate && <div className="space-y-1.5">
                              <Label htmlFor={fieldDate.id} className="text-foreground text-sm font-medium leading-normal flex flex-wrap items-center gap-1 break-words">
                                <span>{fieldDate.label}</span>
                                {fieldDate.required && <span className="text-destructive"> *</span>}
                              </Label>
                              <Input id={fieldDate.id} type="date" value={values[fieldDate.id] || ""} onChange={e => handleInputChange(fieldDate.id, e.target.value)} className="text-base md:text-sm truncate" required={fieldDate.required} />
                              {fieldErrors[fieldDate.id] && <p className="text-xs text-destructive">{fieldErrors[fieldDate.id].message}</p>}
                            </div>}

                          {/* Pickup / Landing Time */}
                          {fieldTime && <div className="space-y-1.5">
                              <Label htmlFor={fieldTime.id} className="text-foreground text-sm font-medium leading-normal flex flex-wrap items-center gap-1 break-words">
                                <span>{fieldTime.label}</span>
                                {fieldTime.required && <span className="text-destructive"> *</span>}
                              </Label>
                              <Input id={fieldTime.id} type={fieldTime.type === "time" ? "time" : "text"} value={values[fieldTime.id] || ""} onChange={e => handleInputChange(fieldTime.id, e.target.value)} placeholder="14:30" className="text-base md:text-sm truncate" required={fieldTime.required} />
                              {fieldErrors[fieldTime.id] && <p className="text-xs text-destructive">{fieldErrors[fieldTime.id].message}</p>}
                            </div>}
                        </div>
                      </div>

                      {/* Return Journey Subgroup (When isRoundTrip) */}
                      {isRoundTrip && <div className="pt-2 border-t border-border">
                          <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground pb-2 border-b border-border mb-4">
                            Rückfahrt
                          </h3>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {/* Return Pickup Date */}
                            {fieldReturnDate && <div className="space-y-1.5">
                                <Label htmlFor={fieldReturnDate.id} className="text-foreground text-sm font-medium leading-normal flex flex-wrap items-center gap-1 break-words">
                                  <span>{fieldReturnDate.label}</span>
                                  <span className="text-destructive"> *</span>
                                </Label>
                                <Input id={fieldReturnDate.id} type="date" value={values[fieldReturnDate.id] || ""} onChange={e => handleInputChange(fieldReturnDate.id, e.target.value)} className="text-base md:text-sm truncate" required />
                                {fieldErrors[fieldReturnDate.id] && <p className="text-xs text-destructive">{fieldErrors[fieldReturnDate.id].message}</p>}
                              </div>}

                            {/* Return Pickup Time */}
                            {fieldReturnTime && <div className="space-y-1.5">
                                <Label htmlFor={fieldReturnTime.id} className="text-foreground text-sm font-medium leading-normal flex flex-wrap items-center gap-1 break-words">
                                  <span>{fieldReturnTime.label}</span>
                                  <span className="text-destructive"> *</span>
                                </Label>
                                <Input id={fieldReturnTime.id} type={fieldReturnTime.type === "time" ? "time" : "text"} value={values[fieldReturnTime.id] || ""} onChange={e => handleInputChange(fieldReturnTime.id, e.target.value)} placeholder="12:00" className="text-base md:text-sm truncate" required />
                                {fieldErrors[fieldReturnTime.id] && <p className="text-xs text-destructive">{fieldErrors[fieldReturnTime.id].message}</p>}
                              </div>}

                            {/* Return Pickup Location */}
                            {fieldReturnLocation && <div className="space-y-1.5">
                                <Label htmlFor={fieldReturnLocation.id} className="text-foreground text-sm font-medium leading-normal flex flex-wrap items-center gap-1 break-words">
                                  <span>{fieldReturnLocation.label}</span>
                                  <span className="text-destructive"> *</span>
                                </Label>
                                <div className="relative">
                                  <MapPin className="size-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                                  <Input id={fieldReturnLocation.id} value={values[fieldReturnLocation.id] || ""} onChange={e => handleInputChange(fieldReturnLocation.id, e.target.value)} placeholder={fieldReturnLocation.placeholder || "Üblicherweise Ihr Hotel"} className="pl-11 text-base md:text-sm truncate" required />
                                </div>
                                {fieldErrors[fieldReturnLocation.id] && <p className="text-xs text-destructive">{fieldErrors[fieldReturnLocation.id].message}</p>}
                              </div>}

                            {/* Return Flight Number */}
                            {fieldReturnFlight && <div className="space-y-1.5">
                                <Label htmlFor={fieldReturnFlight.id} className="text-foreground text-sm font-medium leading-normal flex flex-wrap items-center gap-1 break-words">
                                  <span>{fieldReturnFlight.label}</span>
                                  <span className="text-destructive"> *</span>
                                </Label>
                                <div className="relative">
                                  <Plane className="size-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                                  <Input id={fieldReturnFlight.id} value={values[fieldReturnFlight.id] || ""} onChange={e => handleInputChange(fieldReturnFlight.id, e.target.value)} placeholder={fieldReturnFlight.placeholder || "z. B. DE1403 oder X32145"} className="pl-11 text-base md:text-sm truncate" required />
                                </div>
                                {fieldErrors[fieldReturnFlight.id] && <p className="text-xs text-destructive">{fieldErrors[fieldReturnFlight.id].message}</p>}
                              </div>}

                            {/* Return Destination Airport */}
                            {fieldReturnAirport && <div className="space-y-1.5 sm:col-span-2">
                                <Label htmlFor={fieldReturnAirport.id} className="text-foreground text-sm font-medium leading-normal flex flex-wrap items-center gap-1 break-words">
                                  <span>{fieldReturnAirport.label}</span>
                                  <span className="text-destructive"> *</span>
                                </Label>
                                <div className="relative">
                                  <MapPin className="size-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                                  <Input id={fieldReturnAirport.id} value={values[fieldReturnAirport.id] || ""} onChange={e => handleInputChange(fieldReturnAirport.id, e.target.value)} placeholder={fieldReturnAirport.placeholder || "z. B. Internationaler Flughafen Hurghada (HRG)"} className="pl-11 text-base md:text-sm truncate" required />
                                </div>
                                {fieldErrors[fieldReturnAirport.id] && <p className="text-xs text-destructive">{fieldErrors[fieldReturnAirport.id].message}</p>}
                              </div>}
                          </div>
                        </div>}
                    </div>}

                  {/* STEP 1: Booking Details */}
                  {currentStep === 1 && <div className="space-y-6">
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground pb-2 border-b border-border mb-4">
                          Passagiere & Fahrzeug
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {/* Passengers */}
                          {fieldPax && <div className="space-y-1.5">
                              <Label htmlFor={fieldPax.id} className="text-foreground text-sm font-medium leading-normal flex flex-wrap items-center gap-1 break-words">
                                <span>{fieldPax.label}</span>
                                {fieldPax.required && <span className="text-destructive"> *</span>}
                              </Label>
                              <div className="relative">
                                <Users className="size-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                                <Input id={fieldPax.id} type="number" min="1" max="50" value={values[fieldPax.id] || ""} onChange={e => handleInputChange(fieldPax.id, e.target.value)} placeholder="2" className="pl-11 text-base md:text-sm truncate" required={fieldPax.required} />
                              </div>
                              {fieldErrors[fieldPax.id] && <p className="text-xs text-destructive">{fieldErrors[fieldPax.id].message}</p>}
                            </div>}

                          {/* Suitcases */}
                          {fieldBags && <div className="space-y-1.5">
                              <Label htmlFor={fieldBags.id} className="text-foreground text-sm font-medium leading-normal flex flex-wrap items-center gap-1 break-words">
                                <span>{fieldBags.label}</span>
                                {fieldBags.required && <span className="text-destructive"> *</span>}
                              </Label>
                              <div className="relative">
                                <Briefcase className="size-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                                <Input id={fieldBags.id} type="number" min="0" max="50" value={values[fieldBags.id] || ""} onChange={e => handleInputChange(fieldBags.id, e.target.value)} placeholder="2" className="pl-11 text-base md:text-sm truncate" required={fieldBags.required} />
                              </div>
                              {fieldErrors[fieldBags.id] && <p className="text-xs text-destructive">{fieldErrors[fieldBags.id].message}</p>}
                            </div>}

                          {/* Child Seat Request */}
                          {fieldChildseat && <div className={`space-y-1.5 ${isChildSeatRequested && fieldChildAge ? "sm:col-span-1" : "sm:col-span-2"}`}>
                              <Label htmlFor={fieldChildseat.id} className="text-foreground text-sm font-medium leading-normal flex flex-wrap items-center gap-1 break-words">
                                <span>{fieldChildseat.label}</span>
                                {fieldChildseat.required && <span className="text-destructive"> *</span>}
                              </Label>
                              <div className="relative">
                                <Baby className="size-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10" />
                                {fieldChildseat.options && fieldChildseat.options.length > 0 ? <Select value={values[fieldChildseat.id] || ""} onValueChange={val => handleInputChange(fieldChildseat.id, val)}>
                                    <SelectTrigger id={fieldChildseat.id} className="w-full min-w-0 pl-11 text-base md:text-sm truncate">
                                      <SelectValue placeholder="Option auswählen" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {fieldChildseat.options.map((opt, i) => <SelectItem key={opt.value} value={opt.value} data-index={i} className="text-base md:text-sm truncate">
                                          {opt.label}
                                        </SelectItem>)}
                                    </SelectContent>
                                  </Select> : <Input id={fieldChildseat.id} value={values[fieldChildseat.id] || ""} onChange={e => handleInputChange(fieldChildseat.id, e.target.value)} placeholder="Option auswählen" className="pl-11 text-base md:text-sm truncate" required={fieldChildseat.required} />}
                              </div>
                              {fieldErrors[fieldChildseat.id] && <p className="text-xs text-destructive">{fieldErrors[fieldChildseat.id].message}</p>}
                            </div>}

                          {/* Child Age (Conditional when isChildSeatRequested) */}
                          {isChildSeatRequested && fieldChildAge && <div className="space-y-1.5 sm:col-span-1">
                              <Label htmlFor={fieldChildAge.id} className="text-foreground text-sm font-medium leading-normal flex flex-wrap items-center gap-1 break-words">
                                <span>{fieldChildAge.label || "Alter des Kindes"}</span>
                                <span className="text-destructive"> *</span>
                              </Label>
                              <div className="relative">
                                <Baby className="size-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                                <Input id={fieldChildAge.id} type="number" min="0" max="17" value={values[fieldChildAge.id] || ""} onChange={e => handleInputChange(fieldChildAge.id, e.target.value)} placeholder={fieldChildAge.placeholder || "z. B. 3 (Jahre)"} className="pl-11 text-base md:text-sm truncate" required />
                              </div>
                              {fieldErrors[fieldChildAge.id] && <p className="text-xs text-destructive">{fieldErrors[fieldChildAge.id].message}</p>}
                            </div>}

                          {/* Vehicle Preference */}
                          {fieldVehicle && <div className="space-y-1.5">
                              <Label htmlFor={fieldVehicle.id} className="text-foreground text-sm font-medium leading-normal flex flex-wrap items-center gap-1 break-words">
                                <span>{fieldVehicle.label}</span>
                                {fieldVehicle.required && <span className="text-destructive"> *</span>}
                              </Label>
                              <div className="relative">
                                <Car className="size-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10" />
                                {fieldVehicle.options && fieldVehicle.options.length > 0 ? <Select value={values[fieldVehicle.id] || ""} onValueChange={val => handleInputChange(fieldVehicle.id, val)}>
                                    <SelectTrigger id={fieldVehicle.id} className="w-full min-w-0 pl-11 text-base md:text-sm truncate">
                                      <SelectValue placeholder="Fahrzeugtyp auswählen" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {fieldVehicle.options.map((opt, i) => <SelectItem key={opt.value} value={opt.value} data-index={i} className="text-base md:text-sm truncate">
                                          {opt.label}
                                        </SelectItem>)}
                                    </SelectContent>
                                  </Select> : <Input id={fieldVehicle.id} value={values[fieldVehicle.id] || ""} onChange={e => handleInputChange(fieldVehicle.id, e.target.value)} placeholder="Limousine, Minivan oder Van" className="pl-11 text-base md:text-sm truncate" required={fieldVehicle.required} />}
                              </div>
                              {fieldErrors[fieldVehicle.id] && <p className="text-xs text-destructive">{fieldErrors[fieldVehicle.id].message}</p>}
                            </div>}

                          {/* Hotel / Accommodation */}
                          {fieldHotel && <div className="space-y-1.5">
                              <Label htmlFor={fieldHotel.id} className="text-foreground text-sm font-medium leading-normal flex flex-wrap items-center gap-1 break-words">
                                <span>{fieldHotel.label}</span>
                                {fieldHotel.required && <span className="text-destructive"> *</span>}
                              </Label>
                              <div className="relative">
                                <Building className="size-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                                <Input id={fieldHotel.id} value={values[fieldHotel.id] || ""} onChange={e => handleInputChange(fieldHotel.id, e.target.value)} placeholder={fieldHotel.placeholder || "Name des Hotels oder Resorts"} className="pl-11 text-base md:text-sm truncate" required={fieldHotel.required} />
                              </div>
                              {fieldErrors[fieldHotel.id] && <p className="text-xs text-destructive">{fieldErrors[fieldHotel.id].message}</p>}
                            </div>}
                        </div>
                      </div>
                    </div>}

                  {/* STEP 2: Contact Details */}
                  {currentStep === 2 && <div className="space-y-6">
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground pb-2 border-b border-border mb-4">
                          Kontaktdaten
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {/* Full Name */}
                          {fieldName && <div className="space-y-1.5 sm:col-span-2">
                              <Label htmlFor={fieldName.id} className="text-foreground text-sm font-medium leading-normal flex flex-wrap items-center gap-1 break-words">
                                <span>{fieldName.label}</span>
                                {fieldName.required && <span className="text-destructive"> *</span>}
                              </Label>
                              <div className="relative">
                                <User className="size-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                                <Input id={fieldName.id} value={values[fieldName.id] || ""} onChange={e => handleInputChange(fieldName.id, e.target.value)} placeholder={fieldName.placeholder || "Ihr vollständiger Name"} className="pl-11 text-base md:text-sm truncate" required={fieldName.required} />
                              </div>
                              {fieldErrors[fieldName.id] && <p className="text-xs text-destructive">{fieldErrors[fieldName.id].message}</p>}
                            </div>}

                          {/* Email Address */}
                          {fieldEmail && <div className="space-y-1.5">
                              <Label htmlFor={fieldEmail.id} className="text-foreground text-sm font-medium leading-normal flex flex-wrap items-center gap-1 break-words">
                                <span>{fieldEmail.label}</span>
                                {fieldEmail.required && <span className="text-destructive"> *</span>}
                              </Label>
                              <div className="relative">
                                <Mail className="size-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                                <Input id={fieldEmail.id} type="email" value={values[fieldEmail.id] || ""} onChange={e => handleInputChange(fieldEmail.id, e.target.value)} placeholder={fieldEmail.placeholder || "name@beispiel.de"} className="pl-11 text-base md:text-sm truncate" required={fieldEmail.required} />
                              </div>
                              {fieldErrors[fieldEmail.id] && <p className="text-xs text-destructive">{fieldErrors[fieldEmail.id].message}</p>}
                            </div>}

                          {/* WhatsApp Number */}
                          {fieldWhatsapp && <div className="space-y-1.5">
                              <Label htmlFor={fieldWhatsapp.id} className="text-foreground text-sm font-medium leading-normal flex flex-wrap items-center gap-1 break-words">
                                <span>{fieldWhatsapp.label}</span>
                                {fieldWhatsapp.required && <span className="text-destructive"> *</span>}
                              </Label>
                              <div className="relative">
                                <Phone className="size-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                                <Input id={fieldWhatsapp.id} type="tel" value={values[fieldWhatsapp.id] || ""} onChange={e => handleInputChange(fieldWhatsapp.id, e.target.value)} placeholder={fieldWhatsapp.placeholder || "+49- oder internationales Format"} className="pl-11 text-base md:text-sm truncate" required={fieldWhatsapp.required} />
                              </div>
                              {fieldErrors[fieldWhatsapp.id] && <p className="text-xs text-destructive">{fieldErrors[fieldWhatsapp.id].message}</p>}
                            </div>}
                        </div>
                      </div>

                      {/* Flight & Driver Notes */}
                      {fieldNotes && <div className="space-y-1.5 pt-2 border-t border-border">
                          <Label htmlFor={fieldNotes.id} className="text-foreground text-sm font-medium leading-normal flex flex-wrap items-center gap-1 break-words">
                            <span>{fieldNotes.label}</span>
                            {fieldNotes.required && <span className="text-destructive"> *</span>}
                          </Label>
                          <div className="relative">
                            <MessageSquare className="size-4 text-muted-foreground absolute left-3 top-3 pointer-events-none" />
                            <Textarea id={fieldNotes.id} rows={3} value={values[fieldNotes.id] || ""} onChange={e => handleInputChange(fieldNotes.id, e.target.value)} placeholder={fieldNotes.placeholder || "Flugverspätungen, Zwischenstopps oder Sperrgepäck angeben"} className="pl-11 pt-2.5 min-h-[90px] text-base md:text-sm" required={fieldNotes.required} />
                          </div>
                          {fieldErrors[fieldNotes.id] && <p className="text-xs text-destructive">{fieldErrors[fieldNotes.id].message}</p>}
                        </div>}

                      {/* Additional unmapped schema fields, if any */}
                      {unmappedFields.length > 0 && <div className="space-y-4 pt-2 border-t border-border">
                          {unmappedFields.map((f, i) => <div key={f.id} className="space-y-1.5" data-index={i}>
                              <Label htmlFor={f.id} className="text-foreground text-sm font-medium leading-normal flex flex-wrap items-center gap-1 break-words">
                                <span>{f.label}</span>
                                {f.required && <span className="text-destructive"> *</span>}
                              </Label>
                              {f.options && f.options.length > 0 ? <Select value={values[f.id] || ""} onValueChange={val => handleInputChange(f.id, val)}>
                                  <SelectTrigger id={f.id} className="w-full min-w-0 text-base md:text-sm truncate">
                                    <SelectValue placeholder="Option auswählen" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {f.options.map((opt, optIndex) => <SelectItem key={opt.value} value={opt.value} data-index={optIndex} className="text-base md:text-sm truncate">
                                        {opt.label}
                                      </SelectItem>)}
                                  </SelectContent>
                                </Select> : <Input id={f.id} type={f.type === "email" ? "email" : f.type === "number" ? "number" : "text"} value={values[f.id] || ""} onChange={e => handleInputChange(f.id, e.target.value)} placeholder={f.placeholder || ""} className="text-base md:text-sm truncate" required={f.required} />}
                              {fieldErrors[f.id] && <p className="text-xs text-destructive">{fieldErrors[f.id].message}</p>}
                            </div>)}
                        </div>}
                    </div>}

                  {/* STEP 3: Review & Summary */}
                  {currentStep === 3 && <div className="space-y-6">
                      <div className="pb-2 border-b border-border">
                        <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground">
                          Ihre Buchung überprüfen
                        </h3>
                        <p className="mt-1 text-sm text-muted-foreground">
                          Bitte überprüfen Sie Ihre Reisedaten vor dem Absenden. Klicken Sie auf Bearbeiten, um Angaben zu ändern.
                        </p>
                      </div>

                      {/* Group 1: Fahrtdetails */}
                      {renderReviewGroup("Fahrtdetails", 0, [fieldPickup, fieldDest, fieldType, fieldFlight, fieldDate, fieldTime, ...(isRoundTrip ? [fieldReturnDate, fieldReturnTime, fieldReturnLocation, fieldReturnFlight, fieldReturnAirport] : [])])}

                      {/* Group 2: Buchungsdetails */}
                      {renderReviewGroup("Buchungsdetails", 1, [fieldPax, fieldBags, fieldChildseat, ...(isChildSeatRequested && fieldChildAge ? [fieldChildAge] : []), fieldVehicle, fieldHotel])}

                      {/* Group 3: Kontaktdaten */}
                      {renderReviewGroup("Kontaktdaten", 2, [fieldName, fieldEmail, fieldWhatsapp, fieldNotes, ...unmappedFields])}

                      {/* Form Actions */}
                      <div className="pt-4 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-border mt-8">
                        <p className="text-xs text-muted-foreground order-2 sm:order-1">
                          Feste Preise pro Fahrzeug. Keine Online-Zahlung erforderlich – Bezahlung bei Ankunft.
                        </p>
                        <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto order-1 sm:order-2">
                          <Button type="button" variant="outline" onClick={handleBack} className="w-full sm:w-auto">
                            <ChevronLeft className="size-4 mr-1" />
                            Zurück
                          </Button>
                          <Button type="submit" disabled={submitting} className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-3">
                            {submitting ? <>
                                <Loader2 className="size-4 animate-spin mr-2" />
                                {formDef?.submit?.processing_label || "Anfrage wird gesendet..."}
                              </> : formDef?.submit?.label || "Transferanfrage absenden"}
                          </Button>
                        </div>
                      </div>
                    </div>}

                  {/* Wizard Step Navigation Footer (Steps 0-2) */}
                  {currentStep < 3 && <div className="pt-4 flex flex-col-reverse sm:flex-row items-center justify-between gap-3 border-t border-border mt-8">
                      {currentStep > 0 ? <Button type="button" variant="outline" onClick={handleBack} className="w-full sm:w-auto">
                          <ChevronLeft className="size-4 mr-1" />
                          Zurück
                        </Button> : <div className="hidden sm:block" />}
                      <Button type="button" onClick={handleNext} className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 px-8">
                        Weiter
                        <ChevronRight className="size-4 ml-1" />
                      </Button>
                    </div>}
                </form>
              </motion.div>}
          </div>
        </div>
      </div>
    </section>;
}